import { Middleware } from "redux";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { NEW_INSTANCE_IDENTIFIER } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { Events } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormActivity, FormEngineActions } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";

import { updateIntegratedDocument } from "../../app/integratedDocuments/actions";
import { selectOperationLockStateByGroup } from "../../app/slices/operation/selectors";
import { DOCUMENT_LOCK_GROUP, isProcessLocked } from "../../utilities/lockProcessUtils";

import { lockProcessError } from "../processFormEngine/store/actions";
import { ANTRAG_DETAIL_ACTIVITY_ID, TASK_DETAIL_ACTIVITY_ID } from "../processOverview/store/actions";
import { FRISTEN_ACTIVITY } from "../fristen/constants";
import { dataModels as fristenDataModels } from "../fristen/moduleResources";

import { requestUpdateDueWiedervorlagenCount } from "./actions";
import { WIEDERVORLAGEN_OVERVIEW_ACTIVITY } from "./constants";
import { dataModels } from "./moduleResources";

/**
 * Schließt die Wiedervorlagen-Activity, wenn der Prozess gestartet wird (Event-Button).
 * Andernfalls wird ein Bug ausgelöst, der durch die Interaktion zwischen Workflows und Client entsteht und eine Endlosschleife
 * zur Folge hat.
 */
export const closeWiedervorlagenMiddleware: Middleware = api => next => action => {
	if (FormEngineActions.event.match(action) && Events.eventButton.match(action.payload.engineEvent)) {
		const { engineEvent } = action.payload;
		if (
			engineEvent.payload.name === "proceed" &&
			ActivitySelectors.activityById(WIEDERVORLAGEN_OVERVIEW_ACTIVITY)(api.getState())
		) {
			api.dispatch(ActivityActions.cancel({ activityId: WIEDERVORLAGEN_OVERVIEW_ACTIVITY }));
		}
	}
	return next(action);
};

/**
 * Horcht auf das Commit_Done-Event des Wiedervorlage-Modals, wenn dieses bearbeitet und gespeichert wird.
 * Danach wird eine UpdateIntegratedWiedervorlage-Action dispatcht, die dafür sorgt, dass etwaige integrierte
 * WVL in offenen Formularen auf den soeben gespeicherten Stand aktualisiert werden.
 * Zusätzlich wird die Wiedervorlagen-Overview neu geladen und der Badge-Count aktualisiert.
 */
export const syncWiedervorlagenMiddleware: Middleware = api => next => action => {
	if (ActivityActions.commit.done.match(action)) {
		const store = api.getState();
		const committedActivity = ActivitySelectors.activityById(action.payload.params.activityId)(store);
		if (
			committedActivity?.descriptor?.model === dataModels.WIEDERVORLAGE_D &&
			committedActivity.descriptor.processId
		) {
			const committedData = ActivitySelectors.data(committedActivity.id)(store);
			const isNewDocument =
				!FormActivity.Data.SingleDocumentData.isInstance(committedData) ||
				committedData.document.id === NEW_INSTANCE_IDENTIFIER;

			if (!isNewDocument) {
				api.dispatch(
					updateIntegratedDocument({
						subdocumentId: committedData.document.id.split("/")[1],
						processId: committedActivity.descriptor.processId,
						subdocument: committedData.document
					})
				);
			}

			const wiedervorlagenOverview = ActivitySelectors.activityById(WIEDERVORLAGEN_OVERVIEW_ACTIVITY)(store);
			if (wiedervorlagenOverview) {
				api.dispatch(ActivityActions.reloadData({ activityId: WIEDERVORLAGEN_OVERVIEW_ACTIVITY }));
			}

			const fristenActivity = ActivitySelectors.activityById(FRISTEN_ACTIVITY)(store);
			if (fristenActivity) {
				api.dispatch(ActivityActions.reloadData({ activityId: FRISTEN_ACTIVITY }));
			}

			const antragDetailActivity = ActivitySelectors.activityById(ANTRAG_DETAIL_ACTIVITY_ID)(store);
			if (antragDetailActivity?.initiatingActivityId) {
				api.dispatch(ActivityActions.reloadData({ activityId: antragDetailActivity.initiatingActivityId }));
			}

			api.dispatch(requestUpdateDueWiedervorlagenCount({ activityId: ANTRAG_DETAIL_ACTIVITY_ID }));
		}
	}

	// When a task document (Prozess_WF) is saved, the server may have created or updated a Wiedervorlage
	// via subdocument extraction. Refresh the overview and badge count to reflect those changes.
	if (
		ActivityActions.save.done.match(action) &&
		(action.payload.params.activityId === TASK_DETAIL_ACTIVITY_ID ||
			action.payload.params.activityId === ANTRAG_DETAIL_ACTIVITY_ID)
	) {
		const store = api.getState();
		const wiedervorlagenOverview = ActivitySelectors.activityById(WIEDERVORLAGEN_OVERVIEW_ACTIVITY)(store);
		if (wiedervorlagenOverview) {
			api.dispatch(ActivityActions.reloadData({ activityId: WIEDERVORLAGEN_OVERVIEW_ACTIVITY }));
		}

		api.dispatch(requestUpdateDueWiedervorlagenCount({ activityId: ANTRAG_DETAIL_ACTIVITY_ID }));
	}

	// When a Frist document (with an integrated Wiedervorlage) is committed from the fristen overview modal,
	// reload any open overviews so they reflect the newly created or updated Wiedervorlage.
	// The badge count update is already handled by commitFristMiddleware.
	if (ActivityActions.commit.done.match(action)) {
		const store = api.getState();
		const committedActivity = ActivitySelectors.activityById(action.payload.params.activityId)(store);
		if (
			committedActivity?.descriptor?.model === fristenDataModels.FRIST_D &&
			committedActivity.descriptor.processId
		) {
			const wiedervorlagenOverview = ActivitySelectors.activityById(WIEDERVORLAGEN_OVERVIEW_ACTIVITY)(store);
			if (wiedervorlagenOverview) {
				api.dispatch(ActivityActions.reloadData({ activityId: WIEDERVORLAGEN_OVERVIEW_ACTIVITY }));
			}

			const fristenActivity = ActivitySelectors.activityById(FRISTEN_ACTIVITY)(store);
			if (fristenActivity) {
				api.dispatch(ActivityActions.reloadData({ activityId: FRISTEN_ACTIVITY }));
			}

			const antragDetailActivity = ActivitySelectors.activityById(ANTRAG_DETAIL_ACTIVITY_ID)(store);
			if (antragDetailActivity?.initiatingActivityId) {
				api.dispatch(ActivityActions.reloadData({ activityId: antragDetailActivity.initiatingActivityId }));
			}
		}
	}

	return next(action);
};

/**
 * Prüft vor dem Öffnen des Wiedervorlage-Formulars den Lock-Status des Prozesses.
 * Wenn der Vorgang durch eine andere Person gesperrt ist, wird das Öffnen für Add/Edit blockiert.
 */
export const validateWiedervorlageBeforeEditMiddleware: Middleware = api => next => action => {
	if (FormEngineActions.event.match(action) && Events.eventButtonTriggered.match(action.payload.engineEvent)) {
		const activity = ActivitySelectors.activityById(action.payload.activityId)(api.getState());
		if (activity?.descriptor?.model === dataModels.WIEDERVORLAGE_D) {
			const state = api.getState();
			const user = UaaSelectors.user(state);
			const lockState = selectOperationLockStateByGroup(DOCUMENT_LOCK_GROUP)(state)[0];
			const lockValidationResult = !isProcessLocked(lockState, user);
			if (!lockValidationResult) {
				api.dispatch(lockProcessError({}));
				api.dispatch(ActivityActions.cancel({ activityId: action.payload.activityId }));
				api.dispatch(ActivityActions.cancelRequested({ activityIds: [action.payload.activityId] }));
				api.dispatch(
					ActivityActions.responseCancelConfirmation({ activityId: action.payload.activityId, veto: false })
				);
				return;
			}
		}
	}

	return next(action);
};
